import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RoomOptionGroup } from 'src/Components/Inventory/InventoryItemFields';
import { useCreateBuilding, useCreateRoom } from 'src/Hooks/Mutations/useCartographyMutations';
import useFetchCartographyTree from 'src/Hooks/Queries/useFetchCartographyTree';
import { BuildingType, RoomDto, RoomType } from 'src/types/cartography.types';

/**
 * Rooms of the house for the inventory, and "create the room in one click" when Lucien hears
 * a room that does not exist yet (Alexis, 03/11/2026): the inventory must never wait for the
 * description of the house.
 */
export const useInventoryRooms = () => {
  const { t } = useTranslation();
  const { data: tree, isLoading } = useFetchCartographyTree();
  const { mutateAsync: createBuilding } = useCreateBuilding();
  const { mutateAsync: createRoomMutation, isLoading: isCreatingRoom } = useCreateRoom();

  const buildings = useMemo(() => tree?.buildings ?? [], [tree]);
  const rooms = useMemo(() => buildings.flatMap(building => building.rooms), [buildings]);
  const roomOptions: RoomOptionGroup[] = useMemo(
    () =>
      buildings
        .filter(building => building.rooms.length > 0)
        .map(building => ({
          group: building.name,
          items: building.rooms.map(room => ({ value: room.id, label: room.name })),
        })),
    [buildings]
  );

  const createRoom = async (name: string): Promise<RoomDto> => {
    const buildingId =
      buildings[0]?.id ??
      (
        await createBuilding({
          name: t('inventory.rooms.defaultBuilding', 'Maison'),
          type: BuildingType.MAIN_HOUSE,
        })
      ).id;
    return createRoomMutation({ buildingId, name: name.trim(), type: RoomType.OTHER });
  };

  return { rooms, roomOptions, isLoading, createRoom, isCreatingRoom };
};
